"use client";

import React from "react";
import { Button } from "../../../components/ui/button";
import {
  Lock,
  Download,
  Brain,
  Users,
  Shield,
  Cookie,
  Mail,
  RefreshCw,
  Globe,
  Calendar,
  MessageCircle,
  Edit,
  Trash2,
} from "lucide-react";

const Page = () => {
  return (
    <section className="flex flex-col w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 gap-8">
      <div className="flex flex-col gap-6 w-full">
        <h1 className="font-bricolage font-bold text-yellow text-4xl sm:text-5xl lg:text-[64px] leading-tight text-center sm:text-left">
          Privacy Policy
        </h1>

        <div className="prose prose-lg max-w-none">
          <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-6">
            This Privacy Policy explains how <strong>Outcome Outing</strong>,
            collects, uses, shares, and protects
            your information when you visit our website, interact with us, or
            participate in our trips.
          </p>

          <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-8">
            By using this website or engaging with us, you agree to the terms
            outlined here.
          </p>

          <div className="space-y-8">
            {/* Section 1 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Download className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  1. What Information We Collect
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                We collect the following types of personal information when you
                apply for or participate in a trip:
              </p>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                <li>
                  <strong>Contact information:</strong> Name, email address,
                  phone number
                </li>
                <li>
                  <strong>Personal details:</strong> Age, profession, city,
                  interests (collected via application forms)
                </li>
                <li>
                  <strong>Payment info:</strong> UPI ID, account number (only
                  when required for offline payment – no payment gateway data is
                  stored)
                </li>
                <li>
                  <strong>Technical info:</strong> IP address, browser type,
                  device type (for analytics)
                </li>
              </ul>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                We do <strong>not</strong> collect sensitive personal
                information unless explicitly required for trip planning (e.g.,
                dietary or health-related needs).
              </p>
            </div>

            {/* Section 2 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Brain className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  2. How We Use Your Information
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                We use your data to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                <li>
                  Process trip applications and match you with the right
                  experiences
                </li>
                <li>Communicate important trip updates</li>
                <li>
                  Share mentor or peer information in your trip group (with
                  consent)
                </li>
                <li>Send follow-up communication, guides, or offers</li>
                <li>
                  Improve our website and offerings through aggregated insights
                </li>
                <li>Deliver support through email or WhatsApp</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  3. Sharing & Disclosure
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                We <strong>do not sell or rent your information</strong> to
                third parties.
              </p>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                We only share information:
              </p>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                <li>
                  With <strong>trip facilitators and mentors</strong>, strictly
                  on a need-to-know basis
                </li>
                <li>
                  With <strong>service providers</strong> (e.g., hotels, local
                  hosts) when necessary for your trip
                </li>
                <li>
                  When required by <strong>law or legal obligations</strong>
                </li>
              </ul>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                We ensure all collaborators respect your privacy and handle data
                responsibly.
              </p>
            </div>

            {/* Section 4 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  4. How We Protect Your Data
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                We implement reasonable technical and organizational measures to
                safeguard your personal information. These include:
              </p>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                <li>Secure data storage</li>
                <li>Restricted access within our internal team</li>
                <li>Manual deletion of unneeded data post-trip</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Cookie className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  5. Cookies & Analytics
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                Our website uses cookies and basic analytics tools (e.g., Google
                Analytics) to understand visitor behavior and improve
                performance.
              </p>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                You can adjust cookie settings in your browser at any time.
              </p>
            </div>

            {/* Section 6 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  6. Emails, Updates & Opt-Out
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                By filling out any form on our site, you agree to receive
                trip-related updates or curated content from us. You can opt out
                of any email marketing at any time by clicking the Unsubscribe
                link at the bottom of our emails.
              </p>
            </div>

            {/* Section 7 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <RefreshCw className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  7. Access, Edit, or Delete Your Info
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                You may request to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                <li>View what data we hold about you</li>
                <li>Edit inaccurate information</li>
                <li>Permanently delete your personal data from our system</li>
              </ul>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                Simply email us at <strong>info@outcomeouting.com</strong> with
                your request. We will process it within 7 working days.
              </p>
            </div>

            {/* Section 8 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Globe className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  8. International Visitors
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                Though we primarily serve customers in India, users from other
                countries are welcome. By using our website, you consent to
                having your data processed in India, where privacy laws may
                differ from your home country.
              </p>
            </div>

            {/* Section 9 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  9. Policy Changes
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                We may occasionally update this Privacy Policy to reflect
                improvements or legal requirements. Major changes will be
                notified via email or our website.
              </p>
            </div>

            {/* Section 10 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  10. Contact Us
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-4">
                For any questions, clarifications, or concerns about your
                privacy:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#ffae00]" />
                  <a
                    href="mailto:info@outcomeouting.com"
                    className="font-jakarta font-medium text-[#ffae00] hover:underline"
                  >
                    info@outcomeouting.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#ffae00]" />
                  <a
                    href="https://wa.me/917982579691"
                    className="font-jakarta font-medium text-[#ffae00] hover:underline"
                  >
                    +91 7982579691
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center w-full mt-8">
        <Button
          variant="outline"
          className="font-jakarta font-semibold text-[#ffae00] text-lg sm:text-xl tracking-[0.20px] leading-7 px-6 sm:px-7 py-2.5 rounded-xl border border-solid border-[#f1f1f1] bg-transparent hover:bg-[#ffae00]/10 transition-colors duration-200"
        >
          <Lock className="w-5 h-5 mr-2" />I Understand
        </Button>
      </div>
    </section>
  );
};

export default Page;
